import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetchAllFormQuery, useFetchAllViewQuery } from '@redux/api'

import { createSearchCache } from '@helpers'

import {
  TitleDuplicateValidationMode,
  validateDuplicateTitle,
} from './validateDuplicateTitle'

const titleProp = 'title'

export type TitleEntityType = 'view' | 'form'

type UseEntityTitleDuplicateValidationProps = {
  entityType: TitleEntityType
  defaultValue?: string
}

export const useEntityTitleDuplicateValidation = ({
  entityType,
  defaultValue = '',
}: UseEntityTitleDuplicateValidationProps) => {
  const { t } = useTranslation()
  const isView = entityType === 'view'

  const { data: viewResponse } = useFetchAllViewQuery(undefined, { skip: !isView })
  const { data: formResponse } = useFetchAllFormQuery(undefined, { skip: isView })

  const items = isView ? viewResponse?.data : formResponse?.data

  const titleCache = useMemo(
    () => createSearchCache(items || [], titleProp),
    [items]
  )

  useEffect(() => {
    return () => {
      titleCache.clearCache()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const errorKey = isView ? 'error.duplicateViewTitle' : 'error.duplicateFormTitle'

  return {
    validate: (value: string, mode: TitleDuplicateValidationMode = 'create') => {
      const trimmed = value.trim()
      const hasDuplicate = Boolean(titleCache.find(trimmed))

      return validateDuplicateTitle({
        value,
        mode,
        defaultValue,
        hasDuplicate,
        t,
        errorKey,
      })
    },
  }
}
